import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import StyledEditorInput from 'components/molecules/Editors/EditorInput/EditorInput.styles';
import {useExtensions} from 'helpers/hooks/editor';
import EditorMenu from 'components/molecules/Editors/EditorMenu/EditorMenu';
import EditorLinkPopup from 'components/molecules/Editors/EditorLinkPopup/EditorLinkPopup';
import dom from 'helpers/dom';
import {linkModifier} from 'helpers/hooks/links';

const EditorInput = React.forwardRef((props, ref) => {
  const {
    id,
    name,
    value,
    size,
    mentions,
    extensions,
    minLength,
    maxLength,
    showDialog,
    showMenu,
    onFocus,
    onBlur,
    onChange,
    onDialog,
    onToggle,
    ExtensionProps,
    ...innerProps
  } = useComponentProps(props, 'EditorInput', {
    static: ['showMenu'],
    styled: ['rows', 'minRows', 'maxRows']
  });

  const innerRef = useRef(null);
  const editorRef = useRef(null);
  const valueRef = useRef(null);

  const [internalState, setInternalState] = useState({
    showMenu: false
  });

  const editorInput = useMemo(() => ({
    refs: {
      ref: innerRef,
      editorRef: editorRef
    },
    state: {
      ...internalState,
      ...utils.cleanObject({showMenu})
    },
    value: value,
    focus: () => {
      innerRef.current?.editor?.commands?.focus();
    }
  }), [value, internalState, showMenu]);

  useImperativeHandle(ref, () => editorInput);

  const defaultExtensions = useExtensions({limit: maxLength, mentions, ...ExtensionProps});
  const extensionsMemo = useMemo(() => {
    return (extensions ?? []).concat(defaultExtensions
      .filter((ext1) => !(extensions ?? [])?.find((ext2) => ext1.name.toLowerCase() === ext2.name.toLowerCase())));
  }, [extensions, defaultExtensions]);

  const valueMemo = useMemo(() => {
    if (!utils.isDefined(valueRef.current)) {
      let html = utils.md2html(value ?? '');
      if (mentions) {
        html = utils.editorMentions(html, mentions, true);
      }

      valueRef.current = {
        html: html,
        original: value
      };
    }
    return valueRef.current.html;
  }, [value, mentions]);

  const getHTML = useCallback((editor) => {
    let html = (editor?.getHTML() ?? '').trim();
    // drop unnecessary p wrapper
    if (html.toLowerCase().startsWith('<p>') && html.toLowerCase().endsWith('</p>') &&
      !html.toLowerCase().slice(3, -4).match((/(<p)|(p>)/gi))) {
      html = html.slice(3, -4);
    }

    // convert mentions to default format
    if (mentions) {
      html = utils.editorMentions(html, mentions, false);
    }

    return html;
  }, [mentions]);

  const onDialogEvent = useEffectEvent(onDialog);
  const doDialog = useCallback((editor) => {
    onDialogEvent?.(getHTML(editor), editor);
  }, [getHTML, onDialogEvent]);

  const editor = innerRef.current?.editor;
  useEffect(() => {
    if (editor && !editor?.isDestroyed && valueRef.current?.original !== (value ?? '')) {
      let html = utils.md2html(value ?? '');
      if (mentions) {
        html = utils.editorMentions(html, mentions, true);
      }

      valueRef.current = {
        html: html,
        original: value
      };

      queueMicrotask(() => {
        const currentSelection = innerRef.current?.editor?.state?.selection;
        innerRef.current?.editor?.chain()
          .setContent(valueRef.current.html)
          .setTextSelection(currentSelection)
          .run();
      });
    }
  }, [value, mentions, editor]);

  useEffect(() => {
    if (showDialog && editor && !editor?.isDestroyed) {
      const parent = dom.getParent(innerRef.current?.editor?.view?.dom, null, 'InputContainer');
      if (parent) {
        return utils.observeEvent(parent, 'keydown', (e) => {
          if (linkModifier(e) && e.shiftKey && e.key === 'o') {
            doDialog(innerRef.current?.editor);
          }
        });
      }
    }
  }, [showDialog, editor, doDialog]);

  const handleUpdate = ({editor}) => {
    valueRef.current.html = getHTML(editor);
    valueRef.current.original = valueRef.current.html;

    const e = {
      target: {
        value: valueRef.current.html
      }
    };

    onChange?.(e);
  }

  const handleFocus = ({event}) => {
    onFocus?.(event);
  }

  const handleInputFocus = () => {
    innerRef.current?.editor?.commands?.focus();
  }

  const handleBlur = ({event}) => {
    const editorDom = dom.getParent(innerRef.current?.editor?.view?.dom, null, 'InputContainer');
    if (editorDom && !dom.isPartOfParent(event.relatedTarget, editorDom, ['EditorLinkPopup'])) {
      onBlur?.(event);
    }
  }

  const handleMenuToggle = () => {
    setInternalState((current) => {
      return utils.updater({
        ...current,
        showMenu: !current.showMenu
      })(current);
    });

    onToggle?.();
  }

  const handleMenuDialog = (editor) => {
    doDialog(editor);
  }

  const renderFooter = () => {
    return <EditorMenu anchor="bottom"
                       size={size !== 'medium' ? 'small' : size}
                       variant={editorInput.state.showMenu ? 'standard' : 'collapsed'}
                       showDialog={showDialog}
                       onDialog={handleMenuDialog}
                       onToggle={handleMenuToggle}/>
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {
    showMenu: editorInput.state.showMenu
  });

  return <StyledEditorInput ref={innerRef} {...innerProps}
                            content={valueMemo}
                            extensions={extensionsMemo}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onUpdate={handleUpdate}>
    {() => {
      return <React.Fragment>
        <input id={id} name={name}
               type="text"
               tabIndex={-1}
               style={{
                 position: 'absolute',
                 maxWidth: '0',
                 maxHeight: '0',
                 border: 'none',
                 pointerEvents: 'none',
                 zIndex: -1
               }}
               onFocus={handleInputFocus}/>
        {renderFooter()}
        <EditorLinkPopup />
      </React.Fragment>
    }}
  </StyledEditorInput>
});

EditorInput.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  size: PropTypes.string,
  mentions: PropTypes.array,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  showDialog: PropTypes.bool,
  showMenu: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onDialog: PropTypes.func,
  placeholder: PropTypes.any,
  ExtensionProps: PropTypes.object
};

export default EditorInput;
